"use client";

import { motion } from "framer-motion";
import { ExternalLink, MapPin } from "lucide-react";
import { fadeUp, stagger, viewportOnce } from "@/lib/motion";
import type { University } from "@/lib/universities";

export default function LocationSection({ university }: { university: University }) {
  const mapsUrl = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
    `${university.name} ${university.city}`
  )}`;

  return (
    <motion.section
      variants={stagger(0.09)}
      initial="hidden"
      whileInView="visible"
      viewport={viewportOnce}
      aria-labelledby="location-heading"
    >
      <motion.p
        variants={fadeUp}
        className="flex items-center gap-2.5 text-xs font-bold uppercase tracking-widest text-accent mb-3"
      >
        <MapPin size={14} aria-hidden="true" />
        Standort
      </motion.p>
      <motion.h2
        variants={fadeUp}
        id="location-heading"
        className="font-display font-bold text-2xl sm:text-3xl tracking-tight text-ink mb-5"
      >
        Standort &amp; Anfahrt
      </motion.h2>

      <motion.div
        variants={fadeUp}
        className="card-flat flex flex-col sm:flex-row sm:items-center gap-5 bg-white/60 p-6"
      >
        <span className="w-12 h-12 shrink-0 rounded-xl bg-accent-soft text-accent flex items-center justify-center">
          <MapPin size={22} aria-hidden="true" />
        </span>
        <div className="flex-1">
          <p className="font-display font-semibold text-lg text-ink">
            {university.city}, {university.state}
          </p>
          <p className="text-sm text-ink-soft leading-relaxed">
            {university.name} – Standort {university.city}
          </p>
        </div>
        <a
          href={mapsUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center justify-center gap-2 rounded-full border border-line bg-white px-5 py-3 text-sm font-semibold text-ink hover:border-accent hover:text-accent transition-colors whitespace-nowrap"
        >
          Auf Google Maps öffnen
          <ExternalLink size={14} aria-hidden="true" />
        </a>
      </motion.div>
      <motion.p variants={fadeUp} className="text-xs text-ink-faint leading-relaxed mt-3">
        Aus Datenschutzgründen binden wir keine externe Karte ein – der Link öffnet
        Google Maps in einem neuen Tab.
      </motion.p>
    </motion.section>
  );
}
